import React, { useState, useEffect } from 'react';

export default function PwaInstallBanner({ isOpen, onClose }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    // Detectar si ya está en modo standalone
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    setIsInstalled(isStandalone);

    // Detectar si es dispositivo iOS (iPhone / iPad)
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(isIosDevice);

    // Capturar el evento de instalación de Chromium
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
        if (onClose) onClose();
      }
      setDeferredPrompt(null);
    } else {
      setShowGuide(true);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-4 z-50 animate-fadeIn text-left">
      <div className="bg-slate-900 border border-slate-800 rounded-3xl max-w-lg w-full p-6 md:p-8 relative shadow-2xl space-y-6">
        
        {/* BOTÓN CERRAR */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-slate-400 hover:text-white font-bold text-lg bg-slate-800 w-8 h-8 rounded-full flex items-center justify-center transition cursor-pointer"
        >
          ✕
        </button>

        {/* ENCABEZADO */}
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-600 p-0.5 shadow-xl shrink-0 flex items-center justify-center text-3xl">
            📱
          </div>
          <div>
            <span className="text-[10px] font-black uppercase tracking-widest text-sky-400 bg-sky-950 px-2.5 py-0.5 rounded-full border border-sky-500/30">
              Progressive Web App (PWA)
            </span>
            <h3 className="text-xl font-black text-white mt-1">
              Instalar English EOH en tu Celular
            </h3>
            <p className="text-xs text-slate-400">Acceso directo, rápido y sin conexión desde tu pantalla de inicio</p>
          </div>
        </div>

        {isInstalled ? (
          <div className="bg-emerald-950/60 border border-emerald-500/40 p-4 rounded-2xl space-y-2 text-center">
            <span className="text-3xl">🎉</span>
            <h4 className="text-sm font-black text-emerald-300">¡La aplicación ya está instalada!</h4>
            <p className="text-xs text-slate-300">
              Ya estás disfrutando de English EOH como aplicación nativa en tu dispositivo.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* VENTAJAS DE INSTALAR */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="bg-slate-950/80 p-3 rounded-xl border border-slate-800 flex items-start gap-2.5">
                <span className="text-lg">⚡</span>
                <div>
                  <div className="font-bold text-white">Carga Ultrarrápida</div>
                  <div className="text-[11px] text-slate-400">Sin barras del navegador</div>
                </div>
              </div>
              <div className="bg-slate-950/80 p-3 rounded-xl border border-slate-800 flex items-start gap-2.5">
                <span className="text-lg">📶</span>
                <div>
                  <div className="font-bold text-white">Modo Sin Conexión</div>
                  <div className="text-[11px] text-slate-400">Consulta vocabulario offline</div>
                </div>
              </div>
            </div>

            {/* GUÍA ESPECÍFICA POR DISPOSITIVO */}
            {isIOS || showGuide ? (
              <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-3 text-xs text-slate-300 leading-relaxed">
                <div className="font-bold text-white flex items-center gap-2 text-sm">
                  <span>🍏</span> Pasos para instalar en iPhone / iPad (Safari):
                </div>
                <ol className="space-y-2 list-decimal list-inside text-slate-300">
                  <li>Presiona el botón <strong className="text-white">Compartir</strong> (<span className="text-sky-400 font-bold font-mono">⎋ Compartir</span>) en la barra inferior de Safari.</li>
                  <li>Desliza hacia abajo y selecciona <strong className="text-white">"Agregar a la pantalla de inicio"</strong> (o <strong className="text-white">"Add to Home Screen ➕"</strong>).</li>
                  <li>Toca <strong className="text-sky-400">"Agregar"</strong> en la esquina superior derecha.</li>
                </ol>
                <div className="text-[11px] text-slate-400 italic pt-1">
                  ¡Listo! El icono de English EOH aparecerá junto a tus otras aplicaciones.
                </div>
              </div>
            ) : (
              <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-3 text-xs text-slate-300 leading-relaxed">
                <div className="font-bold text-white flex items-center gap-2 text-sm">
                  <span>🤖</span> Pasos para instalar en Android / Chrome:
                </div>
                <p>
                  Haz clic en el botón de abajo para instalarla automáticamente en tu teléfono o tablet.
                </p>
                <button
                  onClick={handleInstallClick}
                  className="btn-3d btn-3d-emerald w-full py-3.5 text-xs font-black gap-2 shadow-xl"
                >
                  📲 Instalar Aplicación Ahora 🚀
                </button>
              </div>
            )}
          </div>
        )}

        <div className="pt-2 border-t border-slate-800 flex justify-end">
          <button
            onClick={onClose}
            className="btn-3d btn-3d-slate px-5 py-2 text-xs font-bold"
          >
            Cerrar
          </button>
        </div>

      </div>
    </div>
  );
}
